import {Flex, Image} from 'exsportia-components'
import {MenuItemComplexProps} from 'exsportia-components/dist/components/organisms/menu-item'
import {DateTime} from 'luxon'
import {FC, useCallback, useEffect, useMemo, useState} from 'react'
import {useQuery} from 'react-query'
import {useNavigate, useParams} from 'react-router-dom'

import packageInfo from '../../../package.json'
import {MemberQueries} from '../../api/member'
import {UserExsportiaCardModal} from '../../components/user-exsportia-card-modal/user-exsportia-card-modal'
import {langNames} from '../../constants/common'
import {legalFooter, legalPath, toLegalLang} from '../../legal/legal-helpers'
import {LanguagesEnum, TranslatorScope} from '../../locale/types'
import {DownloadAppModal} from '../../modals/download-app-modal/download-app-modal'
import {GetHelpSheet} from '../../pages/contact-us/contact-us-page'
import {useAuthState} from '../../services/auth/hooks'
import {useAppLoading} from '../../services/global/hooks'
import {UserService} from '../../services/user'
import {DropdownMenu} from '../../ui/components/dropdown-menu/dropdown-menu'
import {auth} from '../../utils/firebase'
import {LocaleOptions, setLocale, useLocale} from '../../utils/i18n'
import {isNotNilAndNotEmpty} from '../../utils/logic'
import {useModalActions} from '../../utils/modals'
import {NO_USER_PHOTO} from '../../utils/remote-content'
import {ContactQRModal} from './contact-qr-modal'

export const profileMenuOptions: (props: {
  l: (scope: TranslatorScope, options?: LocaleOptions) => string
  signOut: () => void
  isLoading: boolean
  openQR: () => void
  openProfile: () => void
  openOrders?: () => void
  openVouchers?: () => void
  openRemoveAccount?: () => void
  openHelp?: () => void
  language: LanguagesEnum
  isContact: boolean
}) => (MenuItemComplexProps & {onlyForContacts?: boolean})[] = ({
  l,
  signOut,
  isLoading,
  openProfile,
  openOrders,
  openVouchers,
  openRemoveAccount,
  openHelp,
  language,
  openQR,
  isContact = false,
}) => {
  const defaultMenuOptions: (MenuItemComplexProps & {onlyForContacts?: boolean})[] = [
    {
      name: 'profile',
      text: l('profile'),
      disabled: isLoading,
      onClick: openProfile,
    },
    // The orders page has no other entry point once the customer leaves the
    // store flow (Dima 2026-08-17: "where is users orders").
    ...(openOrders
      ? [
          {
            name: 'orders',
            text: l('store-orders', {caseAction: 'capitalize'}),
            disabled: isLoading,
            onClick: openOrders,
          },
        ]
      : []),
    ...(openVouchers
      ? [
          {
            name: 'vouchers',
            text: l('vouchers_title'),
            disabled: isLoading,
            onClick: openVouchers,
          },
        ]
      : []),
    {
      name: 'qr',
      text: l('qrCode'),
      onClick: openQR,
      disabled: isLoading,
      onlyForContacts: true,
    },
    ...(openHelp
      ? [
          {
            name: 'help',
            text: l('get_help', {caseAction: 'capitalize'}),
            disabled: isLoading,
            onClick: openHelp,
          },
        ]
      : []),
    {
      name: 'language',
      popperPosition: 'bottom',
      popperModifiers: ['offsetLeft'],
      text: l('language', {language: l(language as TranslatorScope), caseAction: 'capitalize'}),
      options: [
        {
          name: 'en',
          text: langNames.en,
          onClick: () => setLocale(LanguagesEnum.EN),
        },
        {
          name: 'heb',
          text: langNames.heb,
          onClick: () => setLocale(LanguagesEnum.HEB),
        },
        {
          name: 'ua',
          text: langNames.ua,
          onClick: () => setLocale(LanguagesEnum.UA),
        },
        {
          name: 'th',
          text: langNames.th,
          onClick: () => setLocale(LanguagesEnum.TH),
        },
        {
          name: 'my',
          text: langNames.my,
          onClick: () => setLocale(LanguagesEnum.MY),
        },
        {
          name: 'ru',
          text: langNames.ru,
          onClick: () => setLocale(LanguagesEnum.RU),
        },
      ],
    },
    {
      name: 'privacy',
      text: l('privacyPolicy'),
      onClick: () => window.open(legalPath('privacy', toLegalLang(language)), '_blank', 'noopener,noreferrer'),
    },
    {
      name: 'terms',
      text: l('termsConditions', {caseAction: 'capitalize'}),
      onClick: () => window.open(legalPath('terms', toLegalLang(language)), '_blank', 'noopener,noreferrer'),
    },
    {
      name: 'dataDeletion',
      text: legalFooter(toLegalLang(language)).deletion,
      onClick: () => window.open(legalPath('deletion', toLegalLang(language)), '_blank', 'noopener,noreferrer'),
    },
    {
      name: 'signOut',
      onClick: signOut,
      text: l('logOut'),
      disabled: isLoading,
    },
    // Customer self-service deletion: the entry the data-deletion page tells people to use.
    ...(openRemoveAccount
      ? [
          {
            name: 'removeAccount',
            text: l('removeAccount'),
            disabled: isLoading,
            onClick: openRemoveAccount,
          },
        ]
      : []),
    {
      name: 'about',
      text: (
        <div style={{fontSize: 12, color: '#9CA3AF', textAlign: 'center', width: '100%'}}>
          {`© ${DateTime.now().toFormat('yyyy')} Education AI Group Co., Ltd. · v.${packageInfo.version}`}
        </div>
      ),
    },
  ]
  if (isContact) {
    return defaultMenuOptions
  }
  return defaultMenuOptions.filter((el) => !el.onlyForContacts)
}

export const ProfileMenu: FC = () => {
  const {l, language} = useLocale()
  const {isLoading} = useAppLoading()
  const {signOut} = useAuthState()
  const {openModal, updateModalConfig} = useModalActions()
  const {venueId} = useParams()
  const navigate = useNavigate()
  const {data: member} = useQuery(
    MemberQueries.getMemberByVenueQuery({query: {venueId, userId: auth.currentUser?.uid}}),
  )
  const {data} = useQuery(UserService.queries.getUserQuery({cacheTime: 0, params: {id: auth.currentUser?.uid}}))
  const isContact = useMemo(() => isNotNilAndNotEmpty(member?.uid), [member?.uid])
  const [imageError, setImageError] = useState(false)
  const imageSrc = useMemo(() => {
    if (imageError || !data?.avatar) {
      return NO_USER_PHOTO
    }
    return data.avatar
  }, [imageError, data?.avatar])

  useEffect(() => {
    setImageError(false)
    if (data?.avatar) {
      const img = new window.Image()
      img.onerror = () => setImageError(true)
      img.onload = () => setImageError(false)
      img.src = data.avatar
    }
  }, [data?.avatar])
  const handleOpenQR = useCallback(() => {
    openModal({
      component: <ContactQRModal venueId={venueId} />,
      footerConfig: {
        disabled: true,
      },
    })
  }, [openModal])
  const handleOpenProfile = useCallback(() => {
    openModal({
      id: 'profile-modal',
      component: (
        <>
          <DownloadAppModal />
          <UserExsportiaCardModal
            name={data?.name || ''}
            avatar={data?.avatar}
            subText={member?.uid || data?.email || ''}
            handleOpenQr={member?.uid ? handleOpenQR : null}
            withDescription={false}
            phone={data?.phone_number || data?.phone}
            phoneClaimed={data?.phone_claimed}
          />
        </>
      ),
      title: l('profile', {caseAction: 'capitalize'}),
      footerConfig: {disabled: true},
    })
  }, [l, openModal, member, data])
  const handleOpenHelp = useCallback(() => {
    openModal({
      title: l('get_help', {caseAction: 'capitalize'}),
      component: <GetHelpSheet venueId={venueId} />,
      footerConfig: {disabled: true},
    })
  }, [l, openModal, venueId])
  const menuOptions = useMemo(
    () =>
      profileMenuOptions({
        l,
        openOrders: () => navigate(`/${venueId}/orders`),
        openVouchers: () => navigate(`/${venueId}/vouchers`),
        openRemoveAccount: () => navigate(`/${venueId}/remove-account`),
        openHelp: handleOpenHelp,
        signOut,
        isLoading,
        openProfile: handleOpenProfile,
        language,
        openQR: handleOpenQR,
        isContact,
      }),
    [l, signOut, isLoading, handleOpenProfile, handleOpenHelp, language, isContact],
  )
  useEffect(() => {
    updateModalConfig('profile-modal', {
      component: (
          <UserExsportiaCardModal
            name={data?.name || ''}
            avatar={data?.avatar}
            subText={member?.uid || data?.email || ''}
            handleOpenQr={member?.uid ? handleOpenQR : null}
            withDescription={false}
            phone={data?.phone_number || data?.phone}
            phoneClaimed={data?.phone_claimed}
          />
      ),
    })
  }, [member?.uid, data?.email, data?.name, data?.avatar, data?.phone, data?.phone_claimed, handleOpenQR, updateModalConfig])

  return (
    <DropdownMenu
      name='profile-menu'
      height='max-content'
      options={menuOptions}
    >
      <Flex
        py='4px'
        cursor={'pointer'}
        justifyContent={'center'}
      >
        <Image
          width={'48px'}
          height={'48px'}
          borderRadius={'24px'}
          src={imageSrc}
          title={data?.name || l('profile')}
        />
      </Flex>
    </DropdownMenu>
  )
}
